import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

const CHERKASY_LAT = 49.44;
const CHERKASY_LON = 32.06;

function latLonToVector3(lat, lon, radius) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

function getSunPosition(radius) {
  const now = new Date();
  const utcHours = now.getUTCHours() + now.getUTCMinutes() / 60;
  const sunLon = -(utcHours - 12) * 15;
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
  const sunLat = 23.44 * Math.sin(((360 / 365) * (dayOfYear - 81)) * (Math.PI / 180));
  return latLonToVector3(sunLat, sunLon, radius);
}

function createGlobe(container, size, enableZoom) {
  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
  camera.position.z = 3;

  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  renderer.setSize(size, size);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);

  // Lights — sun position based on real time (same as nina-savkina-website)
  const ambientLight = new THREE.AmbientLight(0x888899, 2.0);
  scene.add(ambientLight);
  const earthRotationY = -CHERKASY_LON * (Math.PI / 180) - Math.PI / 2;
  const sunPos = getSunPosition(5);
  sunPos.applyAxisAngle(new THREE.Vector3(0, 1, 0), earthRotationY);
  const directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
  directionalLight.position.copy(sunPos);
  scene.add(directionalLight);

  // Earth
  const textureLoader = new THREE.TextureLoader();
  const earthTexture = textureLoader.load('/img/earth-texture.jpg');
  const earthGeometry = new THREE.SphereGeometry(1, 64, 64);
  const earthMaterial = new THREE.MeshPhongMaterial({
    map: earthTexture,
    bumpScale: 0.05,
  });
  const earth = new THREE.Mesh(earthGeometry, earthMaterial);
  scene.add(earth);

  // Atmosphere (blue like original)
  const atmosphereGeometry = new THREE.SphereGeometry(1.05, 64, 64);
  const atmosphereMaterial = new THREE.MeshBasicMaterial({
    color: 0x12c2e9,
    transparent: true,
    opacity: 0.08,
    side: THREE.BackSide,
  });
  scene.add(new THREE.Mesh(atmosphereGeometry, atmosphereMaterial));

  // Cherkasy main marker (bigger, green, pulsing)
  const cherkasyPos = latLonToVector3(CHERKASY_LAT, CHERKASY_LON, 1.02);

  const markerGeometry = new THREE.SphereGeometry(0.035, 16, 16);
  const markerMaterial = new THREE.MeshBasicMaterial({ color: 0x4dc247 });
  const marker = new THREE.Mesh(markerGeometry, markerMaterial);
  marker.position.copy(cherkasyPos);
  earth.add(marker);

  const ringGeometry = new THREE.RingGeometry(0.04, 0.07, 32);
  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0x4dc247,
    transparent: true,
    opacity: 0.6,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeometry, ringMaterial);
  ring.position.copy(cherkasyPos);
  ring.lookAt(new THREE.Vector3(0, 0, 0));
  earth.add(ring);

  // Country markers — where the company operates
  const countries = [
    { name: 'Польща', lat: 52.23, lon: 21.01 },
    { name: 'Румунія', lat: 44.43, lon: 26.10 },
    { name: 'Чехія', lat: 50.08, lon: 14.44 },
    { name: 'Словаччина', lat: 48.15, lon: 17.11 },
    { name: 'Угорщина', lat: 47.50, lon: 19.04 },
    { name: 'Молдова', lat: 47.01, lon: 28.86 },
    { name: 'Болгарія', lat: 42.70, lon: 23.32 },
    { name: 'Литва', lat: 54.69, lon: 25.28 },
    { name: 'Латвія', lat: 56.95, lon: 24.11 },
    { name: 'Естонія', lat: 59.44, lon: 24.75 },
    { name: 'Німеччина', lat: 52.52, lon: 13.40 },
    { name: 'Австрія', lat: 48.21, lon: 16.37 },
    { name: 'Італія', lat: 41.90, lon: 12.50 },
    { name: 'Франція', lat: 48.86, lon: 2.35 },
    { name: 'Нідерланди', lat: 52.37, lon: 4.90 },
    { name: 'Бельгія', lat: 50.85, lon: 4.35 },
  ];

  const smallMarkerGeom = new THREE.SphereGeometry(0.008, 8, 8);
  const smallMarkerMat = new THREE.MeshBasicMaterial({ color: 0x16b724, transparent: true, opacity: 0.9 });

  countries.forEach((c) => {
    const pos = latLonToVector3(c.lat, c.lon, 1.015);
    const dot = new THREE.Mesh(smallMarkerGeom, smallMarkerMat);
    dot.position.copy(pos);
    earth.add(dot);
  });

  // Manual drag controls (since OrbitControls may not be available in this Three.js version)
  let isDragging = false;
  let previousMouse = { x: 0, y: 0 };
  let autoRotate = true;

  if (enableZoom) {
    const onDown = (x, y) => {
      isDragging = true;
      autoRotate = false;
      previousMouse = { x, y };
    };
    const onMove = (x, y) => {
      if (!isDragging) return;
      earth.rotation.y += (x - previousMouse.x) * 0.005;
      earth.rotation.x += (y - previousMouse.y) * 0.003;
      earth.rotation.x = Math.max(-0.8, Math.min(0.8, earth.rotation.x));
      previousMouse = { x, y };
    };
    const onUp = () => {
      isDragging = false;
      // Resume auto-rotate after 3 seconds
      setTimeout(() => { if (!isDragging) autoRotate = true; }, 3000);
    };

    renderer.domElement.addEventListener('mousedown', (e) => onDown(e.clientX, e.clientY));
    renderer.domElement.addEventListener('mousemove', (e) => onMove(e.clientX, e.clientY));
    renderer.domElement.addEventListener('mouseup', onUp);
    renderer.domElement.addEventListener('mouseleave', onUp);
    renderer.domElement.addEventListener('touchstart', (e) => { e.preventDefault(); onDown(e.touches[0].clientX, e.touches[0].clientY); });
    renderer.domElement.addEventListener('touchmove', (e) => { e.preventDefault(); onMove(e.touches[0].clientX, e.touches[0].clientY); });
    renderer.domElement.addEventListener('touchend', onUp);
  }

  earth.rotation.y = -CHERKASY_LON * (Math.PI / 180) - Math.PI / 2;

  let animationId;
  const clock = new THREE.Clock();

  const animate = () => {
    animationId = requestAnimationFrame(animate);
    const time = clock.getElapsedTime();

    // Pulsing ring (same as original)
    const scale = 1 + Math.sin(time * 3) * 0.3;
    ring.scale.set(scale, scale, scale);
    ring.material.opacity = 0.6 - Math.sin(time * 3) * 0.3;

    // Auto rotation
    if (autoRotate) {
      earth.rotation.y += 0.004;
    }

    renderer.render(scene, camera);
  };

  animate();

  return () => {
    cancelAnimationFrame(animationId);
    earthGeometry.dispose();
    earthMaterial.dispose();
    atmosphereGeometry.dispose();
    atmosphereMaterial.dispose();
    markerGeometry.dispose();
    markerMaterial.dispose();
    ringGeometry.dispose();
    ringMaterial.dispose();
    renderer.dispose();
    if (container.contains(renderer.domElement)) {
      container.removeChild(renderer.domElement);
    }
  };
}

// Small globe for footer
function GlobeSmall({ onClick }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    return createGlobe(containerRef.current, 300, false);
  }, []);

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      style={{
        width: 300,
        height: 300,
        margin: '0 auto',
        cursor: 'pointer',
      }}
    />
  );
}

// Big globe modal (same as original)
function GlobeModal({ onClose }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const size = Math.min(window.innerWidth, window.innerHeight) * 0.75;
    return createGlobe(containerRef.current, size, true);
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeInGlobe 0.3s ease',
      }}
    >
      <style>{`
        @keyframes fadeInGlobe {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '30px',
          background: 'transparent',
          border: 'none',
          color: '#fff',
          fontSize: '36px',
          cursor: 'pointer',
          zIndex: 10001,
          opacity: 0.7,
        }}
      >
        ✕
      </button>

      <div
        style={{
          position: 'absolute',
          bottom: '40px',
          textAlign: 'center',
          color: '#fff',
          fontSize: '16px',
          opacity: 0.7,
        }}
      >
        📍 Черкаси, Україна
      </div>

      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        style={{ cursor: 'grab' }}
      />
    </div>
  );
}

function Globe3D() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <GlobeSmall onClick={() => setIsExpanded(true)} />
      {isExpanded && <GlobeModal onClose={() => setIsExpanded(false)} />}
    </>
  );
}

export default Globe3D;
